import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { apiClient, API_BASE } from '@/services/api/client'
import { isEmailVerificationRequired, passwordLogin } from '@/services/api/auth'
import type { PasswordLoginResponse } from '@/services/api/auth'
import { setSentryUser, Sentry, PROFILE_ID_STORAGE_KEY } from '@/lib/sentry'
import { clearPendingDestination } from '@/lib/pendingDestination'
import { identifyPostHogUser, resetPostHog } from '@/lib/posthog'
import { seedSelectedAthleteFromUrl } from '@/contexts/athleteSelection'
import {
  takePendingImpersonationCode,
  hasPendingImpersonationCode,
  markImpersonationSession,
  clearImpersonationFlag,
} from '@/lib/impersonation'

// ============================================================================
// TYPES
// ============================================================================

interface UserProfile {
  id?: number | null
  first_name: string
  last_name: string
  email: string
  profile_id?: number | null
  is_staff?: boolean
  is_superuser?: boolean
  impersonated_by?: { id: number; name: string } | null
  connected_integrations?: string[]
  account_type?: string
  is_onboarding_completed?: boolean
  coach_preferences?: {
    persona?: string
    reply_length?: string
    training_intensity?: string
  }
}

interface AuthContextValue {
  user: UserProfile | undefined
  /** 'pending' while the profile fetch is in flight, 'error' if it failed
   * (auth stays degraded-but-authenticated), 'success' when `user` is known.
   * The seat-freeze gate needs this to hold rendering only while genuinely
   * unknown, without falling into a permanent spinner on profile failure.
   */
  profileStatus: 'pending' | 'error' | 'success'
  isAuthenticated: boolean
  /** True while an impersonation handoff code is being exchanged on boot — the
   * route guards must hold rendering (not redirect to login) until it resolves. */
  isImpExchangePending: boolean
  login: (email: string, password: string) => Promise<PasswordLoginResponse>
  logout: () => void
  authenticateWithTokens: (access: string, refresh: string) => void
}

// ============================================================================
// TOKEN MANAGEMENT (standalone — importable by non-React code)
// ============================================================================

export function getAccessToken(): string | null {
  return localStorage.getItem('access_token')
}

function getRefreshToken(): string | null {
  return localStorage.getItem('refresh_token')
}

/**
 * `profile_id` is valid ONLY for the session that wrote it, so every function
 * that starts or ends one drops it — a new session has not resolved its profile
 * yet, and an ended one must leave nothing behind.
 *
 * It has two readers, and staleness hurts both. The Android shell lifts it when
 * adopting a webapp sign-up session (WebSessionProbe), and `initSentry()` seeds
 * the Sentry user from it so errors thrown before the profile fetch resolves are
 * still attributed. A leftover value would attribute the NEXT person's errors to
 * the previous one — a webview or a shared browser really is handed a different
 * account without an intervening page load.
 */
function forgetProfileId() {
  localStorage.removeItem(PROFILE_ID_STORAGE_KEY)
}

function setTokens(access: string, refresh: string) {
  localStorage.setItem('access_token', access)
  localStorage.setItem('refresh_token', refresh)
  localStorage.removeItem('mobile_webview_auth')
  forgetProfileId()
}

function setMobileWebViewToken(access: string) {
  localStorage.setItem('access_token', access)
  localStorage.removeItem('refresh_token')
  localStorage.setItem('mobile_webview_auth', '1')
  forgetProfileId()
}

// Impersonation sessions are access-only (no refresh token) like the mobile
// webview, but flagged separately so forceLogout/401 handling treats them as a
// normal expiring session (land on login) rather than the webview branch.
function setImpersonationToken(access: string) {
  localStorage.setItem('access_token', access)
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('mobile_webview_auth')
  // Without this the staff member's own id would stay seeded for the whole
  // impersonated session, attributing the athlete's errors to them.
  forgetProfileId()
  markImpersonationSession()
}

/** Webview session teardown: these sessions are access-only, so the access token
 *  IS the session and nothing else may outlive it. */
function clearAccessToken() {
  localStorage.removeItem('access_token')
  forgetProfileId()
}

function clearTokens() {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('mobile_webview_auth')
  forgetProfileId()
  clearImpersonationFlag()
}

/** Exit an impersonated session: drop the token and return the staff member to
 *  the Django admin (their own admin session is untouched by the handoff). */
export function exitImpersonation() {
  clearTokens()
  setSentryUser(null)
  window.location.href = `${API_BASE}/admin/`
}

function consumeAccessTokenFromUrl(routePathname = window.location.pathname): string | null {
  // Email verification carries a verification JWT in `?token=`. Treating it as
  // the native WebView's access token overwrites the real web session shared by
  // every tab and removes its refresh token.
  if (/^\/(?:verify-email|r\/email-verify)(?:\/|$)/.test(routePathname)) return null

  const url = new URL(window.location.href)
  const fragmentParams = new URLSearchParams(url.hash.slice(1))
  const token = url.searchParams.get('token') ?? fragmentParams.get('token')
  if (!token) return null

  url.searchParams.delete('token')
  fragmentParams.delete('token')
  const remainingFragment = fragmentParams.toString()
  url.hash = remainingFragment ? `#${remainingFragment}` : ''
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  return token
}

export function isMobileWebViewAuthSession(): boolean {
  return localStorage.getItem('mobile_webview_auth') === '1'
}

// ============================================================================
// TOKEN REFRESH (mutex — concurrent callers share one in-flight request)
// ============================================================================

let refreshPromise: Promise<string> | null = null

/** The backend runs SIMPLE_JWT with ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION,
 *  so every successful refresh returns a NEW refresh token and blacklists the one we
 *  sent. Persisting the rotated token is mandatory — dropping it leaves localStorage
 *  holding a blacklisted token that 401s on the next refresh and forces a logout. */
async function postRefresh(refresh: string): Promise<string> {
  const { data } = await axios.post(`${API_BASE}/api/token/refresh/`, { refresh })
  const access = data.access as string
  localStorage.setItem('access_token', access)
  if (data.refresh) localStorage.setItem('refresh_token', data.refresh as string)
  return access
}

export async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const refresh = getRefreshToken()
    if (!refresh) throw new Error('No refresh token')
    try {
      return await postRefresh(refresh)
    } catch (err) {
      // The mutex above is per-tab. With rotation on, a second tab that refreshed
      // while our request was in flight has already blacklisted the token we sent.
      // If localStorage now holds a different (freshly rotated) token, that tab won
      // the race — use its token instead of tearing down a session that is alive.
      const rotated = getRefreshToken()
      if (rotated && rotated !== refresh) return await postRefresh(rotated)
      throw err
    }
  })()

  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

/**
 * Called by interceptors (non-React code) when auth is unrecoverable.
 * Clears tokens and hard-redirects to login.
 */
export function forceLogout(reason = 'unknown') {
  // Every involuntary session teardown is reported so a "keeps logging me out"
  // report can be attributed in Sentry/Loki instead of re-diagnosed by hand.
  // A webview session has no refresh token, so the native shell's access token
  // expiring lands here too; the shell normally catches that 401 and reloads with
  // a fresh token (iOS WebViewCoordinator `authError`). That is a handoff, not a
  // logout, so it is counted under its own name (BEO-3182: it was 88% of
  // forced_logout). Whether the shell's reload then succeeded is not visible here.
  const webview = isMobileWebViewAuthSession()
  Sentry.captureMessage(webview ? 'auth.webview_token_rejected' : 'auth.forced_logout', {
    level: webview ? 'info' : 'warning',
    tags: { logout_reason: reason, webview: String(webview) },
  })

  if (webview) {
    clearAccessToken()
    setSentryUser(null)
    return
  }

  clearPendingDestination()
  clearTokens()
  setSentryUser(null)
  // ?expired=1 shows the sign-in page's "Session expired" note; every caller is a failed refresh.
  window.location.href = '/static/login?expired=1'
}

// ============================================================================
// CONTEXT
// ============================================================================

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Must run before first render so RequireAuth doesn't redirect iOS webview loads.
    // Seed the native Lens athlete handover before AthleteProvider initializes.
    seedSelectedAthleteFromUrl()
    const urlToken = consumeAccessTokenFromUrl(location.pathname)
    if (urlToken) {
      setMobileWebViewToken(urlToken)
      return true
    }
    return !!getAccessToken()
  })
  // The handoff code is stashed by main.tsx BEFORE the first render, so this
  // initializer is synchronous: the guards see the pending exchange on render
  // #1 instead of redirecting to /login while the token is still on the wire.
  const [isImpExchangePending, setIsImpExchangePending] = useState(() => hasPendingImpersonationCode())
  const isPublicWorkoutShareRoute = location.pathname.includes('/workout/share/')

  // Impersonation handoff: exchange the one-time code (stashed from the URL
  // fragment in main.tsx) for a short-lived access-only token. Runs once.
  const impRan = useRef(false)
  useEffect(() => {
    if (impRan.current) return
    const code = takePendingImpersonationCode()
    if (!code) return
    impRan.current = true
    setIsImpExchangePending(true)
    axios
      .post(`${API_BASE}/api/v1/auth/impersonation/exchange/`, { code })
      .then(({ data }) => {
        setImpersonationToken(data.access)
        setIsAuthenticated(true)
        queryClient.invalidateQueries({ queryKey: ['user', 'profile'] })
        // Belt and braces: if a protected route already redirected to login
        // before the exchange resolved, land on the intended route now.
        const next = new URLSearchParams(window.location.search).get('next')
        // Local paths only — never hand navigate() an external origin.
        navigate(next?.startsWith('/') ? next : '/', { replace: true })
      })
      .catch(() => {
        navigate('/login?imp_failed=1')
      })
      .finally(() => setIsImpExchangePending(false))
  }, [navigate, queryClient])

  // User profile query — only fires when authenticated
  const { data: user, error: profileError, status: profileStatus } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: async () => {
      const { data } = await apiClient.get<UserProfile>('/auth_app/profile/')
      return data
    },
    enabled: isAuthenticated && !isPublicWorkoutShareRoute,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  // React-level logout on profile 401 (token + refresh both expired).
  // Only fires after the interceptor already tried refresh and failed.
  // 500s/network errors are ignored — user stays logged in (degraded).
  useEffect(() => {
    const status = (profileError as { response?: { status?: number } })?.response?.status
    if (status === 401 && !isPublicWorkoutShareRoute) {
      if (isMobileWebViewAuthSession()) {
        clearAccessToken()
        setSentryUser(null)
        queryClient.clear()
        return
      }

      clearTokens()
      setSentryUser(null)
      queryClient.clear()
      setIsAuthenticated(false)
      navigate('/login?expired=1')
    }
  }, [profileError, queryClient, navigate, isPublicWorkoutShareRoute])

  const login = useCallback(async (email: string, password: string) => {
    const response = await passwordLogin(email, password)
    if (isEmailVerificationRequired(response)) {
      return response
    }

    setTokens(response.access, response.refresh)
    setIsAuthenticated(true)
    return response
  }, [])

  const authenticateWithTokens = useCallback((access: string, refresh: string) => {
    setTokens(access, refresh)
    setIsAuthenticated(true)
    // The tokens may belong to a DIFFERENT account than the one already signed
    // in (verifying an email signs the verified account in). Without this the
    // cached profile keeps serving, and identifying, the previous user for up
    // to its staleTime. Same move as the impersonation exchange above.
    queryClient.invalidateQueries({ queryKey: ['user', 'profile'] })
  }, [queryClient])

  const logout = useCallback(() => {
    clearPendingDestination()
    clearTokens()
    setSentryUser(null)
    resetPostHog()
    queryClient.clear()
    setIsAuthenticated(false)
    navigate('/login')
  }, [navigate, queryClient])

  useEffect(() => {
    setSentryUser(user)
    if (!user) return
    if (user.profile_id != null) {
      // Persisted to localStorage so the Android shell can lift it when adopting
      // a webapp sign-up session (see WebSessionProbe) — the tokens' coupling.
      localStorage.setItem(PROFILE_ID_STORAGE_KEY, String(user.profile_id))
    }
    // profile.id only — never auth.User.id. See identifyPostHogUser for why a
    // fallback would permanently split the person, and what erasure depends on.
    identifyPostHogUser(user.profile_id)
  }, [user])

  // Sync auth state when tokens are cleared externally (e.g. forceLogout from interceptor)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'access_token' && !e.newValue) {
        setIsAuthenticated(false)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const value = useMemo(
    () => ({ user, profileStatus, isAuthenticated, isImpExchangePending, login, logout, authenticateWithTokens }),
    [user, profileStatus, isAuthenticated, isImpExchangePending, login, logout, authenticateWithTokens],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
